import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Clock, RefreshCw, Mail, AlertTriangle, Calendar, Play, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";

const JOB_LABEL = {
  expiring_reminders: "Promemoria scadenze",
};

const formatDateTime = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("it-IT", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

export default function CronRuns() {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/cron-runs");
      setRuns(data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  const runNow = async () => {
    if (!window.confirm("Eseguire ora il cron job? Verranno inviate le email di promemoria scadenze e sospensioni in base ai dati attuali.")) return;
    setRunning(true);
    try {
      const { data } = await api.post("/admin/cron-runs/run-now");
      const r = data.run || {};
      toast.success(`Cron eseguito · ${r.expiring ?? 0} polizze in scadenza, ${r.client_emails_sent ?? 0} email inviate, ${r.skipped_already_sent ?? 0} saltati, ${r.suspended_alerts_sent ?? 0} avvisi sospensione`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRunning(false);
    }
  };

  const clearAll = async () => {
    if (!runs.length) {
      toast.info("La cronologia è già vuota.");
      return;
    }
    if (!window.confirm(`Cancellare TUTTA la cronologia dei cron job (${runs.length} record)? L'operazione è irreversibile.`)) return;
    setClearing(true);
    try {
      const { data } = await api.delete("/admin/cron-runs");
      toast.success(`Cronologia svuotata · ${data.deleted ?? 0} record rimossi`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setClearing(false);
    }
  };

  const deleteOne = async (row) => {
    if (!row?.id) {
      toast.error("Record senza ID: impossibile eliminare singolarmente. Usa 'Svuota Cronologia'.");
      return;
    }
    if (!window.confirm(`Eliminare questa esecuzione del job "${row.job}" del ${formatDateTime(row.ran_at)}?`)) return;
    setDeletingId(row.id);
    try {
      await api.delete(`/admin/cron-runs/${encodeURIComponent(row.id)}`);
      toast.success("Record eliminato");
      // Aggiornamento ottimistico + reload
      setRuns((prev) => prev.filter((r) => r.id !== row.id));
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDeletingId(null);
    }
  };

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="space-y-6 animate-fade-up" data-testid="cron-runs-page">
      <header className="flex items-center justify-between flex-wrap gap-4 print:hidden">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-blue-600 mb-2">Amministrazione</p>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">Cronologia Cron Job</h1>
          <p className="text-slate-500 mt-1">Ultime 30 esecuzioni dei job programmati (promemoria scadenze & sospensioni).</p>
        </div>
        <div className="flex gap-2">
          <Button
            data-testid="run-cron-now"
            onClick={runNow}
            disabled={running}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            <Play className="h-4 w-4 mr-2" /> {running ? "In esecuzione…" : "Esegui ora"}
          </Button>
          <Button variant="outline" onClick={load} data-testid="reload-cron-runs">
            <RefreshCw className="h-4 w-4 mr-2" /> Aggiorna
          </Button>
          <Button
            data-testid="clear-cron-history"
            onClick={clearAll}
            disabled={clearing || !runs.length}
            variant="outline"
            className="border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
            title="Cancella l'intera cronologia dei cron job"
          >
            {clearing ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Trash2 className="h-4 w-4 mr-2" />
            )}
            Svuota Cronologia
          </Button>
        </div>
      </header>

      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <KPI
          icon={Calendar}
          label="Esecuzioni totali"
          value={runs.length}
          accent="bg-blue-50 text-blue-700"
          testid="cron-kpi-runs"
        />
        <KPI
          icon={Mail}
          label="Email scadenza inviate"
          value={runs.reduce((s, r) => s + (r.client_emails_sent || 0), 0)}
          accent="bg-emerald-50 text-emerald-700"
          testid="cron-kpi-emails"
        />
        <KPI
          icon={AlertTriangle}
          label="Avvisi sospensione"
          value={runs.reduce((s, r) => s + (r.suspended_alerts_sent || 0), 0)}
          accent="bg-amber-50 text-amber-700"
          testid="cron-kpi-suspensions"
        />
        <KPI
          icon={Clock}
          label="Promemoria saltati (idempotenza)"
          value={runs.reduce((s, r) => s + (r.skipped_already_sent || 0), 0)}
          accent="bg-slate-100 text-slate-600"
          testid="cron-kpi-skipped"
        />
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-700 text-white text-xs uppercase tracking-wide">
            <tr>
              <th className="text-left px-4 py-3">Job</th>
              <th className="text-left px-4 py-3">Eseguito il</th>
              <th className="text-right px-4 py-3">Polizze in scadenza</th>
              <th className="text-right px-4 py-3">Email scadenza</th>
              <th className="text-right px-4 py-3" title="Reminder saltati perché già inviati (idempotenza)">Saltati</th>
              <th className="text-right px-4 py-3">Avvisi sospensione</th>
              <th className="text-center px-4 py-3 w-20">Azioni</th>
            </tr>
          </thead>
          <tbody data-testid="cron-runs-table">
            {loading && (
              <tr>
                <td colSpan="7" className="text-center py-10 text-slate-400">Caricamento…</td>
              </tr>
            )}
            {!loading && runs.length === 0 && (
              <tr>
                <td colSpan="7" className="text-center py-10 text-slate-400">
                  <Clock className="h-5 w-5 mx-auto mb-2 opacity-50" />
                  Nessuna esecuzione registrata. I job partono alle 09:00 (Europe/Rome).
                </td>
              </tr>
            )}
            {!loading && runs.map((r) => (
              <tr key={r.id || `${r.job}-${r.ran_at}`} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="px-4 py-3">
                  <span className="font-medium text-slate-800">{JOB_LABEL[r.job] || r.job}</span>
                  <div className="text-[11px] text-slate-400 font-mono">{r.job}</div>
                </td>
                <td className="px-4 py-3 text-slate-700 tabular-nums">{formatDateTime(r.ran_at)}</td>
                <td className="px-4 py-3 text-right tabular-nums">{r.expiring ?? 0}</td>
                <td className="px-4 py-3 text-right tabular-nums text-emerald-700 font-medium">{r.client_emails_sent ?? 0}</td>
                <td className="px-4 py-3 text-right tabular-nums text-slate-500" data-testid={`skipped-${r.id}`} title="Reminder non inviati perché già spediti">
                  {r.skipped_already_sent ?? 0}
                </td>
                <td className="px-4 py-3 text-right tabular-nums text-amber-700 font-medium">{r.suspended_alerts_sent ?? 0}</td>
                <td className="px-2 py-2 text-center">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => deleteOne(r)}
                    disabled={deletingId === r.id || !r.id}
                    className="h-8 w-8 text-red-600 hover:bg-red-50 hover:text-red-700 disabled:opacity-40"
                    title={r.id ? "Elimina questo record" : "Record legacy senza ID: usa 'Svuota Cronologia'"}
                    data-testid={`delete-cron-run-${r.id || 'legacy'}`}
                  >
                    {deletingId === r.id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Trash2 className="h-4 w-4" />
                    )}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-slate-400">
        Il cron giornaliero gira alle <strong>09:00</strong> ora Europa/Roma. Invia promemoria ai clienti per polizze in scadenza a 30/15/7/3/1 gg e per polizze sospese a 15/7/3/1 gg dalla data massima di riattivazione.
      </p>
    </div>
  );
}

function KPI({ icon: Icon, label, value, accent, testid }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid={testid}>
      <div className={`h-9 w-9 rounded-md flex items-center justify-center mb-3 ${accent}`}>
        <Icon className="h-4 w-4" />
      </div>
      <p className="text-xs uppercase tracking-wide text-slate-500">{label}</p>
      <p className="text-2xl font-display font-semibold mt-1 tabular-nums">{value}</p>
    </div>
  );
}
